import React from 'react';
import { MemberKind } from '../../../services/UsageService';

export const DEEP = '#2a1ca8';
export const BAR_MUTED = '#e4e0f9';
export const BORDER = '#efebe2';

export const num = (n: number | null | undefined) => Number(n || 0).toLocaleString();

/** "September 2026" / "Sep" for a YYYY-MM month (UTC, like billing). */
export const monthLabel = (month: string, locale: string, short = false) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(locale, {
    month: short ? 'short' : 'long',
    year: short ? undefined : 'numeric',
    timeZone: 'UTC',
  });
};

/** "Today, 19:05" · "Yesterday" · "3 days ago" · "Aug 14". */
export const lastActiveLabel = (
  iso: string | null,
  locale: string,
  t: (k: string, o?: any) => string,
): string => {
  if (!iso) return '—';
  const when = new Date(iso);
  const now = new Date();
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(when)) / 86400000);
  if (days <= 0) {
    return t('usage.members.today', {
      defaultValue: 'Today, {{time}}',
      time: when.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
    });
  }
  if (days === 1) return t('usage.members.yesterday', { defaultValue: 'Yesterday' });
  if (days < 7) return t('usage.members.daysAgo', { defaultValue: '{{count}} days ago', count: days });
  return when.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
};

const AVATAR: Record<MemberKind, { bg: string; fg: string }> = {
  person: { bg: '#eceafd', fg: DEEP },
  system: { bg: '#fdf0e1', fg: '#8a4b0c' },
  support: { bg: '#e6f4ee', fg: '#1d6b4a' },
};

// First character of the first two words, skipping words that start with
// punctuation or a digit. Works for scripts without case (Hebrew, Arabic…).
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => w.length > 0 && !/^[\s\d!-/:-@[-`{-~·•–—]/.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '·';

export const MemberAvatar: React.FC<{ name: string; kind: MemberKind; size?: number }> = ({ name, kind, size = 36 }) => (
  <div
    aria-hidden="true"
    className="rounded-full flex items-center justify-center font-bold flex-shrink-0"
    style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background: AVATAR[kind].bg, color: AVATAR[kind].fg }}
  >
    {kind === 'system' ? '@' : initials(name)}
  </div>
);

/** Localized display name for the built-in buckets; people keep their own name. */
export const memberName = (key: string, name: string, t: (k: string, o?: any) => string) => {
  if (key === 'channel') return t('usage.members.channel', { defaultValue: 'Email & WhatsApp' });
  if (key === 'guest') return t('usage.members.guest', { defaultValue: 'Guest links' });
  if (key === 'api') return t('usage.members.api', { defaultValue: 'API' });
  if (name === 'Unknown user') return t('usage.members.unknownUser', { defaultValue: 'Unknown user' });
  return name;
};

export const memberRole = (role: string, t: (k: string, o?: any) => string) => {
  const keys: Record<string, string> = {
    Admin: 'usage.members.roleAdmin',
    Member: 'usage.members.roleMember',
    'Cata team': 'usage.members.roleCata',
    'Former member': 'usage.members.roleFormer',
    Automatic: 'usage.members.roleAutomatic',
    'No advisor': 'usage.members.roleNoAdvisor',
  };
  return keys[role] ? t(keys[role], { defaultValue: role }) : role;
};
